import React, { useCallback, useEffect, useState } from 'react';
import { fetchMultiBrandAccounts, fetchUserInfo } from '@/redux/user/userActions';
import { updateBrandName } from '@/services/user.service';
import { MULTIBRAND_TOKENS_ARRAY } from '@/utils/multibrand-utils';
import { useDispatch, useSelector } from 'react-redux';

import EditOutlineIcon from '@/components/Icons/icons/EditOutlineIcon';

import CustomTooltip from '../../CustomTooltip/CustomTooltip';
import InputComponent from '../../InputComponent/InputComponent';
import ShortString from '../../ShortString/ShortString';
import SwitchButton from '../SwitchButton';

interface Props {
  setShowChooseAccountModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowProfileDropdown: React.Dispatch<React.SetStateAction<any>>;
  selectedBrand: string | null;
  setSelectedBrand: React.Dispatch<React.SetStateAction<string | null>>;
}

const SwitchContainer: React.FC<Props> = ({
  setShowChooseAccountModal,
  setShowProfileDropdown,
  selectedBrand,
  setSelectedBrand,
}) => {
  const { userDetails, multiBrands } = useSelector((state: any) => state.user);

  const [isEditing, setIsEditing] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');

  const dispatch = useDispatch();

  const getCurrentBrand = useCallback(() => {
    return multiBrands.find((brand: any) => brand.connectedBrandUserId === userDetails?._id);
  }, [multiBrands, selectedBrand]);

  const handleClickBrandEdit = () => {
    setIsEditing(true);
    setNewBrandName(userDetails?.brandName || '');
  };

  const handleUpdateBrandName = async () => {
    if (!newBrandName?.length || newBrandName.length <= 0) {
      setIsEditing(false);
      if (userDetails?.brandName) {
        setNewBrandName(userDetails?.brandName);
      }
      return;
    }

    const body = { brandName: newBrandName };

    const editBrandName = await updateBrandName(body);
    if (editBrandName.status) {
      setIsEditing(false);
      dispatch(fetchUserInfo());
      dispatch(fetchMultiBrandAccounts(MULTIBRAND_TOKENS_ARRAY));
    }
  };

  useEffect(() => {
    if (userDetails?.brandName) {
      setNewBrandName(userDetails?.brandName);
    }
  }, [userDetails?.brandName]);

  return (
    <div className={`header-component__users-container w-full ${isEditing ? 'editing' : ''}`}>
      <div className="w-full header-component__users-top-section flex flex-between items-center">
        <div className="flex flex-start items-center gap-2">
          {isEditing ? (
            <InputComponent
              value={newBrandName}
              onKeyDown={(e) => e.stopPropagation()}
              onChange={(e) => {
                setNewBrandName(e.target.value);
              }}
              onBlur={() => {
                handleUpdateBrandName();
              }}
              inputClassName="text-sm"
              inputWrapperClass="py-1 px-1"
              containerClassName="brand-name-input"
            />
          ) : (
            <ShortString
              name={newBrandName || 'Add Brand Name'}
              className="font-medium text-base"
              stringLength={14}
              placement="bottom"
            />
          )}
          {!isEditing && (
            <CustomTooltip title="Edit Brand Name">
              <div onClick={handleClickBrandEdit}>
                <EditOutlineIcon color="var(--icon-subdued)" />
              </div>
            </CustomTooltip>
          )}
        </div>

        <SwitchButton
          onClick={() => {
            setShowChooseAccountModal(true);
            setShowProfileDropdown(null);
            const currentBrand = getCurrentBrand();
            if (currentBrand && multiBrands.length > 1) {
              setSelectedBrand(currentBrand.connectedBrandTeamMemberId);
            }
          }}
        />
      </div>
    </div>
  );
};

export default SwitchContainer;
